// Canonical number/price/duration formatters — use these instead of local
// helpers or inline toFixed / toLocaleString. All numeric output is en-US
// locale (thousands separators) — except formatPriceForInput, separator-free
// by design; non-finite input never throws — every formatter has a
// documented degenerate output.

import { formatEther } from 'viem';
import { assertDefined } from '@/modules/assert-defined';

function pickDecimals(abs: number): number {
  if (!Number.isFinite(abs) || abs === 0) return 2;
  if (abs >= 1) return 2;
  // Stryker disable next-line Regex: equivalent — only reached for 0 < abs < 1, where toFixed(20) always starts "0.", so the anchor cannot change the match
  const match = /^0\.(0*)/.exec(abs.toFixed(20));
  // Stryker disable next-line StringLiteral: unreachable — group 1 of the regex always captures (possibly empty) on a successful match; the ?? fallback exists only to satisfy noUncheckedIndexedAccess
  const leadingZeros = match ? (match[1] ?? '').length : 0;
  return Math.max(leadingZeros + 3, 4);
}

// Magnitude-aware asset-price formatter: ≥1 → exactly 2dp with separators;
// non-zero sub-1 magnitudes → at least 4 decimals (leading zeros + 3
// significant digits); zero → "0.00"; non-finite → "0". maxDecimals caps the
// fractional digits, and a non-zero value below 10^-maxDecimals renders the
// "<0.0001"-style floor instead.
export function formatPrice(
  n: number,
  opts: { maxDecimals?: number } = {},
): string {
  if (!Number.isFinite(n)) return '0';
  const abs = Math.abs(n);
  const { maxDecimals } = opts;
  if (
    // Stryker disable next-line ConditionalExpression: equivalent — forced true, the floor branch still needs abs < Math.pow(10, -undefined), which is NaN-false
    maxDecimals !== undefined &&
    abs > 0 &&
    abs < Math.pow(10, -maxDecimals)
  ) {
    return `<0.${'0'.repeat(maxDecimals - 1)}1`;
  }
  let d = pickDecimals(abs);
  // Stryker disable next-line ConditionalExpression,EqualityOperator: equivalent — the undefined-guard forced true still caps nothing (d > undefined is false), and >= differs only at d === maxDecimals, where the assignment writes the same value
  if (maxDecimals !== undefined && d > maxDecimals) d = maxDecimals;
  return n.toLocaleString('en-US', {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
}

// Smallest price increment consistent with formatPrice's rendered precision
// at this magnitude: 10^-d for the decimal count formatPrice picks (uncapped).
// For chart price scales whose tick step must resolve below a cent —
// lightweight-charts' default minMove 0.01 renders every sub-cent label as
// 0.00. Zero and non-finite → 0.01. Computed as 1/10^d — correctly-rounded
// division lands on the decimal literal, where Math.pow(10, -d) sits one
// ULP below it.
export function priceTickSize(n: number): number {
  return 1 / Math.pow(10, pickDecimals(Math.abs(n)));
}

// USD amount, sign before the "$", always 2dp: zero and non-finite → "$0";
// magnitudes under a cent → "<$0.01" / "<-$0.01".
export function formatUsd(n: number): string {
  if (!Number.isFinite(n) || n === 0) return '$0';
  // Stryker disable next-line EqualityOperator: equivalent — >= differs only at n === 0, returned '$0' above
  if (n > 0 && n < 0.01) return '<$0.01';
  // Stryker disable next-line EqualityOperator: the <= variant is equivalent (n === 0 returned above); the -0.01 boundary variant is pinned by the one-cent test
  if (n < 0 && n > -0.01) return '<-$0.01';
  const abs = Math.abs(n);
  // Stryker disable next-line EqualityOperator: equivalent — <= differs only at n === 0, returned '$0' above
  return `${n < 0 ? '-$' : '$'}${abs.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// USD amount rounded to the whole dollar, sign before the "$": "$7,135,353",
// "-$12". Zero, non-finite and magnitudes under half a dollar → "$0".
export function formatUsdWhole(n: number): string {
  if (!Number.isFinite(n)) return '$0';
  const whole = Math.round(Math.abs(n));
  if (whole === 0) return '$0';
  // Stryker disable next-line EqualityOperator: equivalent — <= differs only at n === 0, returned '$0' above
  return `${n < 0 ? '-$' : '$'}${whole.toLocaleString('en-US')}`;
}

// A coin quantity: three significant digits below one coin ("0.00105" —
// the size sits past the zeros, where fixed decimals print none of it),
// formatFixed2's up-to-two decimals from one to a thousand ("912.03"),
// whole coins from a thousand up ("27,762") — the fraction stops mattering
// where the integer part carries the size. Non-finite renders "—".
export function formatCoinQty(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  // Stryker disable next-line EqualityOperator: equivalent — at exactly one both branches print "1"
  if (abs < 1) {
    return n.toLocaleString('en-US', { maximumSignificantDigits: 3 });
  }
  // Stryker disable next-line EqualityOperator: equivalent — at exactly a thousand both branches print "1,000"
  if (abs < 1000) return formatFixed2(n);
  return Math.round(n).toLocaleString('en-US');
}

// Wei → exact token-amount display: ÷1e18 through viem's formatEther, string
// math end to end — wei never becomes a float (Number() on a 20-digit wei
// carry-rounds, e.g. 1e20-1 wei to a clean 100 tokens). The fraction is
// truncated to exactly `decimals` digits and zero-padded — round-down, never
// nearest, so a displayed amount never exceeds what's on chain; the integer
// part gets en-US thousands separators. 0n renders as the padded zero
// ("0.000000"-style), and sub-precision dust truncates to the same padded
// zero — any "<0.0001"-style floor is the caller's policy. Domain:
// non-negative wei, decimals ≥ 1.
export function formatWei(wei: bigint, decimals: number): string {
  const s = formatEther(wei);
  const dot = s.indexOf('.');
  const int = dot === -1 ? s : s.slice(0, dot);
  const frac = dot === -1 ? '' : s.slice(dot + 1);
  return `${BigInt(int).toLocaleString('en-US')}.${frac
    .slice(0, decimals)
    .padEnd(decimals, '0')}`;
}

// A price in its quote currency: the dollar sign for the USD and USDT quotes
// ("$1,234.50"), the quote's ticker after the digits otherwise ("0.0123 BTC");
// the digits are formatPrice's.
export function formatQuotedPrice(n: number, quoteAsset: string): string {
  const usd = quoteAsset === 'USD' || quoteAsset === 'USDT';
  return usd ? `$${formatPrice(n)}` : `${formatPrice(n)} ${quoteAsset}`;
}

// Plain price string for seeding <input defaultValue> — no thousands
// separators. ≥1000 → 2dp, ≥1 → 4dp, <1 → 6 significant digits;
// non-finite → "0".
export function formatPriceForInput(n: number): string {
  if (!Number.isFinite(n)) return '0';
  const abs = Math.abs(n);
  if (abs >= 1000) return n.toFixed(2);
  if (abs >= 1) return n.toFixed(4);
  return n.toPrecision(6);
}

// Plain percent string for a percent <input>: up to 2 fractional digits,
// no thousands separators; non-finite → "".
export function formatPctForInput(n: number): string {
  if (!Number.isFinite(n)) return '';
  return String(Math.round(n * 100) / 100);
}

// Up to 2 fractional digits, trailing zeros dropped. Accepts backend numeric
// strings; non-numeric input is returned as-is. Use for percentages and
// performance metrics.
export function formatFixed2(n: number | string): string {
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  // `+ 0` folds the -0 a tiny negative rounds to into 0, so it prints "0",
  // never "-0".
  // Stryker disable next-line ObjectLiteral: equivalent — the value is pre-rounded to 2dp, and k/100 doubles sit within ~1 ULP of exact, so the locale default (max 3 digits, min 0) renders the identical string
  return (Math.round(num * 100) / 100 + 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

// Compact duration from ms: the most-significant non-zero unit plus the next
// unit down, which may be zero — "2d 3h", "8d 0h", "5h 12m", "45s".
// Non-positive/non-finite → "—"; positive sub-second → "0s".
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  const s = Math.floor(ms / 1000);
  const units: [number, string][] = [
    [86400, 'd'],
    [3600, 'h'],
    [60, 'm'],
    [1, 's'],
  ];
  const parts: string[] = [];
  let rest = s;
  for (const [size, label] of units) {
    if (parts.length === 2) break;
    const v = Math.floor(rest / size);
    if (v > 0 || parts.length > 0) {
      parts.push(`${v}${label}`);
      rest -= v * size;
    }
  }
  return parts.length > 0 ? parts.join(' ') : '0s';
}

// Live elapsed time from ms, always down to seconds so a per-second ticker
// visibly moves: leading zero units dropped, zero mid-units kept —
// "2d 0h 4m 5s", "4m 5s", "5s". Non-positive/non-finite → "0s".
export function formatElapsed(ms: number): string {
  // Stryker disable next-line EqualityOperator: equivalent — ms === 0 renders '0s' through the normal path too (every unit floors to zero, seconds always pushed)
  if (!Number.isFinite(ms) || ms <= 0) return '0s';
  const total = Math.floor(ms / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const parts: string[] = [];
  if (d > 0) parts.push(`${d}d`);
  if (d > 0 || h > 0) parts.push(`${h}h`);
  if (d > 0 || h > 0 || m > 0) parts.push(`${m}m`);
  parts.push(`${s}s`);
  return parts.join(' ');
}

// k/M/B/T abbreviation for tight UI slots, 1dp mantissa, sign kept. The tier
// is picked from the mantissa rounded to 1dp, so 999_960 → "1M", never
// "1,000k". Below the k tier falls back to formatFixed2; non-finite renders
// "—".
export function formatCompact(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const tiers: [number, string][] = [
    [1e12, 'T'],
    [1e9, 'B'],
    [1e6, 'M'],
    [1e3, 'k'],
  ];
  for (const [size, suffix] of tiers) {
    if (Math.round((abs / size) * 10) / 10 >= 1) {
      return `${roundTo1(n / size)}${suffix}`;
    }
  }
  return formatFixed2(n);
}

function roundTo1(n: number): string {
  // Stryker disable next-line ObjectLiteral: equivalent — the value is pre-rounded to 1dp, and k/10 doubles sit within ~1 ULP of exact, so the locale default (max 3 digits, min 0) renders the identical string
  return (Math.round(n * 10) / 10).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
}

// Compact relative timestamp: "just now" (<1min — future dates clamp here
// too) / "5m ago" / "3h ago" / "2d ago" / "4mo ago" / "1y ago". Pure in `now`
// (epoch ms — useNow's clock in render): the label ticks exactly with the
// clock it is handed, where a hidden Date.now() would freeze under the React
// Compiler. An invalid date renders "—".
export function formatTimeAgo(date: Date, now: number): string {
  if (Number.isNaN(date.getTime())) return '—';
  const diff = Math.max(0, now - date.getTime());
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  // Month tier covers everything under a year; floor(d/30) reaches 12 at
  // 360-364 days, which must render "11mo ago", never "12mo"/"0y".
  if (d < 365) return `${Math.min(Math.floor(d / 30), 11)}mo ago`;
  const y = Math.floor(d / 365);
  return `${y}y ago`;
}

// Absolute UTC timestamp: "Aug 20, 2026 · 16:10 UTC" — en-US short month,
// zero-padded 24h clock, always UTC regardless of the viewer's zone. Invalid
// dates render "—" (an unparseable wire timestamp must never fabricate a
// real-looking time).
export function formatUtcDateTime(date: Date): string {
  if (Number.isNaN(date.getTime())) return '—';
  const day = date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const hh = String(date.getUTCHours()).padStart(2, '0');
  const mm = String(date.getUTCMinutes()).padStart(2, '0');
  return `${day} · ${hh}:${mm} UTC`;
}

// The year-less UTC timestamp of a dense table, in two parts so a narrow
// cell can stack them: `day` "Jun 5", `time` "07:30 UTC" (zero-padded 24h
// clock, always UTC). Invalid dates render "—" and an empty time.
export function formatUtcDayTime(date: Date): { day: string; time: string } {
  if (Number.isNaN(date.getTime())) return { day: '—', time: '' };
  const day = date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
  });
  const hh = String(date.getUTCHours()).padStart(2, '0');
  const mm = String(date.getUTCMinutes()).padStart(2, '0');
  return { day, time: `${hh}:${mm} UTC` };
}

// Exactly 1 fractional digit. Same numeric-string / pass-through contract as
// formatFixed2.
export function formatFixed1(n: number | string): string {
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  // Rounded first so the -0 a tiny negative becomes folds into 0 (`+ 0`)
  // and prints "0.0", never "-0.0".
  return (Math.round(num * 10) / 10 + 0).toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

// A signed percentage: an explicit "+" on positive values and none on zero
// (a "+0%" fabricates a direction); the negative sign comes with the number.
// Non-finite renders "—".
export function formatSignedPct(n: number, decimals: 1 | 2): string {
  if (!Number.isFinite(n)) return '—';
  const fixed = decimals === 2 ? formatFixed2(n) : formatFixed1(n);
  const zero = fixed === '0' || fixed === '0.0';
  // Stryker disable next-line EqualityOperator: equivalent — n === 0 formats to '0' / '0.0', which `zero` already catches, so > and >= are indistinguishable here
  return `${n > 0 && !zero ? '+' : ''}${fixed}%`;
}

// Price-axis tick labels, formatted as ONE axis: every tick shares a
// notation — M/k tiers from a thousand up, chosen on the largest tick, plain
// digits below, and once every tick is under a thousandth the subscript
// notation micro-cap charts use ("0.0₅34" = 0.0000034 — the subscript counts
// the zeros after the point, the digits after it are the first significant
// ones) — and the fewest digits the tick step needs. Ticks on an exact grid
// (every one a whole multiple of the step, as bucket edges are) take the
// step's own digits, which render each of them exactly; irregular ticks (a
// domain divided evenly) take the digits whose last one resolves half a
// step, so each rounded label stays nearer its own tick than a neighbour's.
// A flat axis (every tick equal) shows three significant digits. Non-finite
// ticks render "—" and stay out of the choice; a zero tick renders "0" in
// every notation.
export function formatAxisTicks(ticks: number[]): string[] {
  const finite = ticks.filter((v) => Number.isFinite(v));
  const maxAbs = Math.max(0, ...finite.map(Math.abs));
  const scale =
    maxAbs >= 1_000_000 ? 1_000_000
    : maxAbs >= 1_000 ? 1_000
    : 1;
  const suffix =
    scale === 1_000_000 ? 'M'
    : scale === 1_000 ? 'k'
    : '';
  const step = tickStep(finite);
  const decimals =
    step > 0 ?
      finite.every((v) => isWhole(v / step)) ?
        decimalsOf(step / scale)
      : Math.max(0, Math.ceil(-Math.log10(step / scale / 2)))
    : 2 - Math.floor(Math.log10(maxAbs / scale));
  const subscript = maxAbs < 0.001;
  const significant = Math.max(2, decimals - leadingZeros(maxAbs));
  return ticks.map((v) => {
    if (!Number.isFinite(v)) return '—';
    if (v === 0) return '0';
    // Stryker disable next-line EqualityOperator: equivalent — a zero tick returned "0" above
    const sign = v < 0 ? '-' : '';
    const abs = Math.abs(v);
    if (subscript) return sign + subscriptLabel(abs, significant);
    return `${sign}${(abs / scale).toFixed(decimals)}${suffix}`;
  });
}

// The smallest non-zero distance between neighbouring ticks; 0 when no two
// neighbours differ.
function tickStep(ticks: number[]): number {
  const gaps = ticks.slice(1).map((v, i) => {
    const prev = ticks[i];
    // Stryker disable next-line CallExpression: equivalent — never fires: slice(1)'s index i always names an element of ticks
    assertDefined(prev);
    return Math.abs(v - prev);
  });
  const positive = gaps.filter((d) => d > 0);
  return positive.length === 0 ? 0 : Math.min(...positive);
}

// Whether `n` is an integer but for float noise (3.0000000000000004).
function isWhole(n: number): boolean {
  // Stryker disable next-line EqualityOperator: equivalent — <= differs only at an exact float equality the tolerance exists to blur
  return Math.abs(n - Math.round(n)) < 1e-9;
}

// The decimal digits of a step: the fewest at which every multiple of it
// renders exactly (0.025 → 3, 0.5 → 1, 20 → 0), twelve at most — past that
// the labels stop telling ticks apart rather than growing without bound.
function decimalsOf(step: number): number {
  let d = 0;
  // Stryker disable next-line EqualityOperator,ConditionalExpression: equivalent — the bound is a termination guard for a step finer than any chart produces; off by one or absent it labels nothing differently
  while (d < 12 && !isWhole(step * 10 ** d)) d += 1;
  return d;
}

// The zeros between the point and the first significant digit of 0 < v < 1.
function leadingZeros(v: number): number {
  return Math.ceil(-Math.log10(v)) - 1;
}

// The subscript notation with `n` significant digits, the rounding allowed
// to carry into the next decade (0.000996 at two digits is "0.0₂10").
function subscriptLabel(abs: number, n: number): string {
  let zeros = leadingZeros(abs);
  let digits = Math.round(abs * 10 ** (zeros + n));
  if (digits >= 10 ** n) {
    digits /= 10;
    zeros -= 1;
  }
  const sub = String(zeros)
    .split('')
    .map((d) => '₀₁₂₃₄₅₆₇₈₉'[+d])
    .join('');
  return `0.0${sub}${digits}`;
}
